/**
 * Photo Cache Service
 *
 * Caches user avatar photos in localStorage to prevent HTTP 429 rate limiting from Google Photos.
 * Similar to menu caching pattern - stores base64 data URLs for instant display.
 */

// Cache key prefix
const CACHE_KEY_PREFIX = 'swan_avatar_';

/**
 * Load cached photo from localStorage
 * @param {string} userId - User ID for cache key
 * @param {string} photoURL - Current photo URL to validate cache
 * @returns {Object|null} Object with {base64Data, needsUpdate} or null if not found
 */
export const loadPhotoFromCache = (userId, photoURL) => {
  if (!userId) return null;

  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);

    // Check if cache needs update (URL changed)
    const needsUpdate = photoURL && cacheEntry.photoURL !== photoURL;

    // Return cached photo even if URL changed (prevents flash)
    // The Avatar component will update in background if needed
    return {
      base64Data: cacheEntry.base64Data,
      needsUpdate
    };
  } catch (error) {
    // Clear corrupted cache
    try {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${userId}`);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
};

/**
 * Fetch photo from URL, convert to base64, and cache in localStorage
 * @param {string} userId - User ID for cache key
 * @param {string} photoURL - Photo URL to fetch
 * @returns {Promise<string|null>} Base64 data URL or null if failed
 */
export const fetchAndCachePhoto = async (userId, photoURL) => {
  if (!userId || !photoURL) return null;

  try {
    let fetchURL = photoURL;
    let fetchOptions = {
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    };

    // Handle Google Photos CORS by using Google's CDN with different parameters
    if (photoURL.includes('googleusercontent.com')) {
      // Add size parameter to use Google's CDN which has better CORS support
      fetchURL = photoURL.includes('?') 
        ? `${photoURL}&sz=96` 
        : `${photoURL}?sz=96`;
      fetchOptions.mode = 'no-cors'; // For Google Images, we need no-cors mode
    } else {
      fetchOptions.mode = 'cors';
    }

    // Fetch the image
    const response = await fetch(fetchURL, fetchOptions);

    if (!response.ok && response.type !== 'opaque') {
      console.error('[PhotoCache] ❌ Fetch failed:', response.status);
      return null;
    }

    // For opaque responses (no-cors), we can't read the blob
    if (response.type === 'opaque') {
      console.warn('[PhotoCache] ⚠️ CORS blocked - using fallback approach');
      // Try direct image loading as fallback
      return await fetchImageDirectly(photoURL, userId);
    }

    // Convert to blob
    const blob = await response.blob();

    // Convert blob to base64 using FileReader
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Cache the result
    const cacheEntry = {
      photoURL,
      base64Data,
      timestamp: Date.now()
    };

    localStorage.setItem(`${CACHE_KEY_PREFIX}${userId}`, JSON.stringify(cacheEntry));

    return base64Data;
  } catch (error) {
    console.error('[PhotoCache] ❌ Failed to fetch/cache photo:', error);
    // Fallback to direct image loading
    try {
      return await fetchImageDirectly(photoURL, userId);
    } catch (fallbackError) {
      console.error('[PhotoCache] ❌ Fallback also failed:', fallbackError);
      return null;
    }
  }
};

/**
 * Fallback: Load image using Image element and canvas to bypass CORS
 * @param {string} photoURL - Photo URL to load
 * @param {string} userId - User ID for cache key
 * @returns {Promise<string|null>} Base64 data URL or null if failed
 */
const fetchImageDirectly = (photoURL, userId) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Try to allow cross-origin
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const base64Data = canvas.toDataURL('image/jpeg', 0.8);
        
        // Cache the result
        const cacheEntry = {
          photoURL,
          base64Data,
          timestamp: Date.now()
        };
        localStorage.setItem(`${CACHE_KEY_PREFIX}${userId}`, JSON.stringify(cacheEntry));
        
        resolve(base64Data);
      } catch (error) {
        console.error('[PhotoCache] Canvas conversion failed:', error);
        resolve(null);
      }
    };
    img.onerror = () => {
      console.warn('[PhotoCache] Image loading failed completely');
      resolve(null);
    };
    img.src = photoURL;
  });
};

/**
 * Clear cached photo for a user (e.g., on logout)
 * @param {string} userId - User ID to clear cache for
 */
export const clearPhotoCache = (userId) => {
  if (!userId) return;

  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}${userId}`);
  } catch (error) {
    console.warn('[PhotoCache] Failed to clear cache:', error);
  }
};

/**
 * Clear all photo caches (e.g., on logout when userId unknown)
 */
export const clearAllPhotoCaches = () => {
  try {
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('[PhotoCache] Failed to clear all caches:', error);
  }
};
